import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Search, Business, UploadFile } from "@mui/icons-material";
import { useCompanies, usePipeline, usePipelineValue } from "@/hooks/useCRM";
import { PIPELINE_LABELS, PIPELINE_COLORS, type PipelineStage } from "@/types";

function PipelineBoard() {
  const navigate = useNavigate();
  const { data: pipeline, isLoading } = usePipeline();
  const { data: pipelineValue } = usePipelineValue();

  if (isLoading) return <CircularProgress size={24} />;

  const stages = Object.entries(PIPELINE_LABELS) as [PipelineStage, string][];
  const formatCurrency = (v: number | null) => (v ? `£${v.toLocaleString()}` : "");

  return (
    <Box>
      {pipelineValue && pipelineValue.grand_total > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Active pipeline value: <strong>£{pipelineValue.grand_total.toLocaleString()}</strong>
        </Alert>
      )}
      <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 2 }}>
        {stages.map(([stage, label]) => {
          const opps = pipeline?.[stage] ?? [];
          const stageValue = pipelineValue?.stages[stage]?.total_value ?? 0;
          return (
            <Box
              key={stage}
              sx={{
                minWidth: 200,
                flex: 1,
                bgcolor: "#fafafa",
                borderRadius: 1,
                p: 1,
                borderTop: `3px solid ${PIPELINE_COLORS[stage]}`,
              }}
            >
              <Typography variant="caption" fontWeight={600} sx={{ display: "block" }}>
                {label} ({opps.length})
              </Typography>
              {stageValue > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  £{stageValue.toLocaleString()}
                </Typography>
              )}
              {opps.map((opp) => {
                const isLead = opp.entity_type === "company_lead";
                return (
                  <Card
                    key={`${opp.entity_type}-${opp.id}`}
                    sx={{
                      mb: 0.5,
                      cursor: "pointer",
                      "&:hover": { boxShadow: 2 },
                      // Distinguish pre-opportunity company leads with a
                      // lighter, dashed card so the at-a-glance state is
                      // clear: still pre-opportunity vs has-opportunity.
                      ...(isLead && {
                        bgcolor: "background.paper",
                        border: "1px dashed",
                        borderColor: "divider",
                        boxShadow: "none",
                      }),
                    }}
                    onClick={() => navigate(`/crm/companies/${opp.company_id}`)}
                  >
                    <CardContent sx={{ py: 1, px: 1.5, "&:last-child": { pb: 1 } }}>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {opp.title}
                        {isLead && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 0.5, fontStyle: "italic" }}
                          >
                            (no opportunity yet)
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {isLead
                          ? opp.industry || "Company lead"
                          : opp.company_name +
                            (opp.quote_value ? ` · ${formatCurrency(opp.quote_value)}` : "") +
                            (opp.engagement_type === "retainer" && opp.contract_start_date
                              ? " /mo"
                              : "")}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default function CRMCompanies() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState<PipelineStage | "">("");

  const {
    data: companies,
    isLoading,
    error,
  } = useCompanies({
    pipeline_stage: stageFilter || undefined,
    search: search || undefined,
  });

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Business color="action" />
        <Typography variant="h4" sx={{ flex: 1 }}>
          CRM
        </Typography>
        <Button
          variant="outlined"
          startIcon={<UploadFile />}
          onClick={() => navigate("/crm/import")}
          sx={{ mr: 1 }}
        >
          Bulk import
        </Button>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate("/crm/companies/new")}
        >
          New Company
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Companies" />
        <Tab label="Pipeline" />
      </Tabs>

      {tab === 1 && <PipelineBoard />}

      {tab === 0 && (
        <>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search companies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} /> }}
              sx={{ minWidth: 250 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Pipeline Stage</InputLabel>
              <Select
                value={stageFilter}
                label="Pipeline Stage"
                onChange={(e) => setStageFilter(e.target.value as PipelineStage | "")}
              >
                <MenuItem value="">All</MenuItem>
                {Object.entries(PIPELINE_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {isLoading && <CircularProgress />}
          {error && <Alert severity="error">Failed to load companies.</Alert>}
          {companies && companies.length === 0 && (
            <Alert severity="info">
              No companies found. Add your first company to get started.
            </Alert>
          )}

          {companies?.map((company) => (
            <Card key={company.id} sx={{ mb: 1.5, "&:hover": { boxShadow: 3 } }}>
              <CardActionArea onClick={() => navigate(`/crm/companies/${company.id}`)}>
                <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {company.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[company.industry, company.city].filter(Boolean).join(" · ") || "No details"}
                    </Typography>
                  </Box>
                  {company.tags?.map((tag) => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" />
                  ))}
                  <Chip
                    label={PIPELINE_LABELS[company.pipeline_stage]}
                    size="small"
                    sx={{
                      bgcolor: PIPELINE_COLORS[company.pipeline_stage],
                      color: "#333",
                      fontWeight: 600,
                    }}
                  />
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </>
      )}
    </Box>
  );
}
